"use strict";
// this utility provides conversion from DataBase records to tape-builder
// records. This should be the only place where DbTable is referenced.
// the conversion is exposed by tape builder only to make this easy
// for consumers of tape builder. it might as well be duplicated
// in all callers.
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.convertor = void 0;
const common_1 = require("@finley-technologies/common");
const finley_types_1 = require("@finley-technologies/finley-types");
const typeguards = __importStar(require("./typeguards"));
const converters_1 = require("./uiNodes/converters");
const uiNodes_1 = require("./uiNodes/uiNodes");
const validation_1 = require("./validation");
const createInputTapeBufferMap = (buffersWithId) => new Map(buffersWithId.map(({ id, buffer }) => [id, buffer]));
const artifactsDocumentFromDbRecords = ({ dbView, dbGraph, artifactDocumentId, }) => {
    const dataTapes = dbGraph.dataTapes.map(common_1.utils.stripDbTimeStamps);
    const dataTapeDependencies = dbGraph.dataTapeDependencies.map(common_1.utils.stripDbTimeStamps);
    if (!dbView) {
        const result = (0, validation_1.validateDataTapes)({
            dataTapeDependencies,
            dataTapes,
        });
        (0, common_1.finleyAssert)(result.isValid, `artifactsDocumentFromDbRecords found errors : [${result.errorMessages.join(',')}]`);
    }
    const artifactsDocument = JSON.stringify({
        dbView,
        dataTapes,
        dataTapeDependencies,
        artifactDocumentId,
    });
    return artifactsDocument;
};
const dbRecordsFromArtifactsDocument = ({ artifactsDocument, }) => {
    let json;
    try {
        json = JSON.parse(artifactsDocument);
    }
    catch (e) {
        throw new Error(`dbRecordsFromArtifactsDocument: not valid JSON: ${artifactsDocument}`);
    }
    const { dataTapes: dataTapesMaybeWithTimeStamps, dataTapeDependencies: dataTapeDependenciesMaybeWithTimeStamps, dbView, artifactDocumentId, } = json;
    (0, common_1.finleyAssert)(typeof artifactDocumentId === 'string', 'artifactDocumentId is missing');
    (0, common_1.finleyAssert)(dbView !== undefined, 'dbView is missing');
    (0, common_1.finleyAssert)(Array.isArray(dataTapesMaybeWithTimeStamps), `dataTapes is not an array: ${dataTapesMaybeWithTimeStamps}`);
    (0, common_1.finleyAssert)(Array.isArray(dataTapeDependenciesMaybeWithTimeStamps), `dataTapeDependencies is not an array: ${dataTapeDependenciesMaybeWithTimeStamps}`);
    // these objects comings from db has created_at/updated_at fields.
    // although the type definitions do not include these fields.
    // strip them out so that jsonschema validation later can pass.
    // TODO: can revisit how typings are being managed here when passed into
    // "validateDataTapes", thus, why setting purely as "any" for now
    const dataTapes = dataTapesMaybeWithTimeStamps.map(common_1.utils.stripDbTimeStamps);
    const dataTapeDependencies = dataTapeDependenciesMaybeWithTimeStamps.map(common_1.utils.stripDbTimeStamps);
    dataTapes.forEach((artifact) => {
        // ensure that artifact is a valid TapeBuilderRecord.
        // TapeBuilderRecord is artifact with strict types for configuration.
        // database adds created_at/updated_at fields to the artifact.
        if (dbView) {
            (0, common_1.finleyAssert)(typeguards.isTapeBuilderRecordDB(artifact), `artifact is not a valid TapeBuilderRecordDB: ${JSON.stringify(artifact)}`);
        }
        else {
            (0, common_1.finleyAssert)(typeguards.isTapeBuilderRecordUI(artifact), `artifact is not a valid isTapeBuilderRecordUI: ${JSON.stringify(artifact)}`);
        }
    });
    dataTapeDependencies.forEach((dependency) => {
        (0, common_1.finleyAssert)(finley_types_1.typeguards.isArtifactDependencyRecord(dependency), `dependency is not a valid DependencyRecord: ${dependency}`);
    });
    if (!dbView) {
        // more logical validations on the tapes and dependencies.
        const result = (0, validation_1.validateDataTapes)({ dataTapeDependencies, dataTapes });
        (0, common_1.finleyAssert)(result.isValid, `dbRecordsFromArtifactsDocument found errors: [${result.errorMessages.join(',')}]`);
    }
    return { dataTapeDependencies, dataTapes, dbView, artifactDocumentId };
};
// Database Graph has edge configurations at the destinations.
// TapeBuilder (Operations) Graph has edge configurations in dbView nodes.
// This function converts the Database Graph into TapeBuilderGraph.
const artifactsDocumentToTapeBuilder = ({ artifactsDocument, }) => {
    const converted = dbRecordsFromArtifactsDocument({
        artifactsDocument,
    });
    const tapeBuilderNodes = [];
    const tapeBuilderEdges = [];
    if (converted.dbView) {
        const { dataTapes, dataTapeDependencies, artifactDocumentId } = converted;
        const edges = dataTapeDependencies.map((dependency) => {
            return {
                source: dependency.inputArtifactId,
                target: dependency.outputArtifactId,
            };
        });
        // for dbView we do not split operations nodes separately.
        // all nodes are treated similarly as `db_nodes`.
        const nodes = dataTapes.map((dataTape) => ({
            metadata: {
                tapeBuilderNodeId: dataTape.id,
                tapeBuilderNodeType: 'db_node',
                artifactDocumentId: dataTape.artifactDocumentId,
            },
            configuration: dataTape,
        }));
        return {
            artifactDocumentId,
            graph: { nodes, edges },
            dbView: true,
        };
    }
    // nodeMap maintains dbGraph node id => tapeBuilder node id
    // for the generated tapes, this will map to operation node.
    const nodeMap = new Map();
    const { dataTapes, dataTapeDependencies, artifactDocumentId } = converted;
    dataTapes.forEach((dataTape) => {
        const { id } = dataTape;
        const converter = (0, converters_1.getConverterByArtifactType)(dataTape.type);
        const { operationNode, tapeNode } = converter.intermediateToUINodes(dataTape);
        if (operationNode !== null) {
            tapeBuilderNodes.push(operationNode);
            tapeBuilderEdges.push({
                source: operationNode.metadata.tapeBuilderNodeId,
                target: tapeNode.metadata.tapeBuilderNodeId,
            });
        }
        tapeBuilderNodes.push(tapeNode);
        // original node maps to either operation node or dest node
        const destId = operationNode
            ? operationNode.metadata.tapeBuilderNodeId
            : tapeNode.metadata.tapeBuilderNodeId;
        nodeMap.set(id, destId);
    });
    // fixup edges
    dataTapeDependencies.forEach((edge) => {
        const { inputArtifactId, outputArtifactId } = edge;
        const sourceTapeBuilderNodeId = inputArtifactId;
        const targetTapeBuilderNodeId = nodeMap.get(outputArtifactId);
        (0, common_1.finleyAssert)(sourceTapeBuilderNodeId !== undefined, `sourceTapeBuilderNodeId is undefined for source ${inputArtifactId}`);
        (0, common_1.finleyAssert)(targetTapeBuilderNodeId !== undefined, `targetTapeBuilderNodeId is undefined for target ${outputArtifactId}`);
        tapeBuilderEdges.push({
            source: sourceTapeBuilderNodeId,
            target: targetTapeBuilderNodeId,
        });
    });
    return {
        artifactDocumentId,
        graph: {
            nodes: tapeBuilderNodes,
            edges: tapeBuilderEdges,
        },
        dbView: false,
    };
};
const tapeBuilderToArtifactsDocument = ({ graph, creditFacilities, loanProducts, artifactDocumentId, createTapeBuilder, }) => {
    const errors = (0, validation_1.validateTapeBuilderNodeGraph)({
        graph,
    });
    if (errors.length > 0) {
        return {
            isValid: false,
            errors,
        };
    }
    // UI graph has operations nodes, which are not stored in the database.
    // here we remove operations and merge their configs into destination nodes.
    const dbGraph = (0, converters_1.detachOperationNodes)({ graph, artifactDocumentId });
    const tapeBuilder = createTapeBuilder(dbGraph.dataTapes);
    // before we return the artifacts document, we need more validations to ensure
    // that the operation definitions are valid.
    // We do this here, because we need full graph to validate the operations.
    const operationErrors = (0, validation_1.validateTapeBuilderOperationDefinitions)({
        dbGraph,
        tapeBuilder,
    });
    if (operationErrors.length > 0) {
        return {
            isValid: false,
            errors: operationErrors,
        };
    }
    // finally, validate any references in tape config
    // Let individual nodes validate their config, and collect
    // the errors here.
    const creditFacilitySlugs = creditFacilities.map(({ slug }) => slug);
    const loanProductSlugs = loanProducts.map(({ slug }) => slug);
    const configErrors = [];
    dbGraph.dataTapes.forEach((dataTape) => {
        const uiNode = (0, uiNodes_1.getUITapeNodeByArtifactType)(dataTape.type);
        const nodeConfigErrors = uiNode.validateIntermediateTape({
            graph: dbGraph,
            tape: dataTape,
            tapeBuilder,
            creditFacilitySlugs,
            loanProductSlugs,
        });
        configErrors.push(...nodeConfigErrors);
    });
    if (configErrors.length > 0) {
        return {
            isValid: false,
            errors: configErrors,
        };
    }
    const artifactsDocument = artifactsDocumentFromDbRecords({
        dbGraph,
        dbView: false,
        artifactDocumentId,
    });
    return {
        isValid: true,
        artifactsDocument,
    };
};
exports.convertor = {
    // conversions for using with ITapeBuilder interface
    createInputTapeBufferMap, // buffersWithId ==> inputTapeBufferMap
    // conversions below are used by translation between server, gql and client.
    // Client (TapeBuilderRecords) => GQL (artifactDocument) => Server (DbRecords)
    // Server (DbRecords) => GQL (artifactDocument) => Client (TapeBuilderRecords)
    // Server <=> GQL
    artifactsDocumentFromDbRecords, // artifactDocument ==> DbRecords
    dbRecordsFromArtifactsDocument, // DbRecords ==> artifactDocument
    // GQL <=> UI (Operation Graph)
    artifactsDocumentToTapeBuilder, // artifactDocument ==> TapeBuilderNodes
    tapeBuilderToArtifactsDocument, // TapeBuilderNodes ==> artifactDocument
};
//# sourceMappingURL=convertor.js.map